import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { dialModeLabels, formatInteger, formatPhoneDisplay, type DialMode } from '@brimo/shared';
import { Banner, Button, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { startSession, useListStart, type ListStart } from '../api';
import { Crumb, DialerPage, Eyebrow, Fact, FactRow, LoadError } from '../ui';
import styles from './start.module.css';

type Filled = Extract<ListStart, { empty: false }>;
type Empty = Extract<ListStart, { empty: true }>;

/** „Die Liste führt 24 Kontakte. 11 sind außerhalb …, 9 haben einen Status …, und 4 liegen …" (03). */
export function emptyReason(data: Empty): string {
  const parts: string[] = [];
  if (data.outsideWindow > 0)
    parts.push(`${formatInteger(data.outsideWindow)} sind außerhalb ihres Zeitfensters`);
  if (data.statusNotDialable > 0) {
    parts.push(`${formatInteger(data.statusNotDialable)} haben einen Status, der nicht angewählt wird`);
  }
  if (data.lockedByOthers > 0) {
    parts.push(`${formatInteger(data.lockedByOthers)} liegen in der Queue eines anderen Setters`);
  }
  const head =
    data.total === 1
      ? 'Die Liste führt 1 Kontakt.'
      : `Die Liste führt ${formatInteger(data.total)} Kontakte.`;
  if (parts.length === 0) return head;
  const last = parts.pop() ?? '';
  return `${head} ${parts.length > 0 ? `${parts.join(', ')}, und ${last}` : last}.`;
}

/**
 * Terminierung 02/03 — the preview before the first ring of a single list (facts, „Queue starten · N", the
 * Power/Preview switch, the first contact in navy) or, with nothing dialable, the reason in figures and the
 * setter's best other list („Stattdessen").
 */
export function PreviewPage() {
  const params = useParams();
  const listId = params['listId'] ?? '';
  const start = useListStart(listId);
  const data = start.data;
  const shortName = data ? (data.listName.split(' · ')[0] ?? data.listName) : null;
  const crumb = <Crumb>{data?.empty ? `Dialer · ${shortName}` : 'Dialer · Vorschau'}</Crumb>;

  if (start.isError) {
    const refused = start.error instanceof ApiError && start.error.status < 500;
    return (
      <DialerPage crumb={crumb}>
        {refused ? (
          <Banner tone="warning">{errorMessage(start.error)}</Banner>
        ) : (
          <LoadError
            error={start.error}
            what="Die Liste"
            retrying={start.isFetching}
            onRetry={() => void start.refetch()}
          />
        )}
      </DialerPage>
    );
  }

  return (
    <DialerPage crumb={crumb}>
      {!data ? <PreviewSkeleton /> : data.empty ? <EmptyQueue data={data} /> : <Preview data={data} />}
    </DialerPage>
  );
}

function PreviewSkeleton() {
  return (
    <div className={styles.previewGrid} aria-busy="true">
      <div className={styles.previewCard}>
        <Skeleton width={120} height={11} />
        <div className={styles.h2}>
          <Skeleton width={360} height={30} />
        </div>
        <div className={styles.previewFacts}>
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className={styles.previewFact}>
              <Skeleton width={100} height={11} />
              <Skeleton width={180} height={17} />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.navyCard}>
        <Skeleton width={160} height={24} />
      </div>
    </div>
  );
}

function Preview({ data }: { data: Filled }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<DialMode>('power');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin() {
    setPending(true);
    setError(null);
    try {
      const state = await startSession({ listIds: [data.listId], dialMode: mode });
      queryClient.setQueryData(['dialer', 'session', 'current'], state);
      if (state.session) void navigate(`/dialer/session/${state.session.id}`);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  const facts: Array<{ label: string; value: string }> = [
    { label: 'Wählbar jetzt', value: `${formatInteger(data.dialable)} von ${formatInteger(data.total)}` },
    { label: 'Modus', value: dialModeLabels[mode].fact },
    { label: 'Ereignistyp', value: data.eventTypeLine ?? '—' },
    { label: 'Rufnummern-Pool', value: data.pool ? `${data.pool.label} · ${data.pool.mask}` : '—' },
    { label: 'Reihenfolge', value: data.orderText },
  ];

  return (
    <div className={styles.previewGrid}>
      <section className={styles.previewCard} aria-labelledby="dialer-preview-title">
        <Eyebrow>Vor dem Wählen</Eyebrow>
        <h2 id="dialer-preview-title" className={styles.h2}>
          {data.listName}
        </h2>
        <dl className={styles.previewFacts}>
          {facts.map((fact) => (
            <div key={fact.label} className={styles.previewFact}>
              <dt>
                <Eyebrow>{fact.label}</Eyebrow>
              </dt>
              <dd className={styles.previewFactValue}>{fact.value}</dd>
            </div>
          ))}
        </dl>
        {error ? (
          <div className={styles.error}>
            <Banner tone="danger">{error}</Banner>
          </div>
        ) : null}
        <div className={styles.ctaRow}>
          <button
            type="button"
            className={styles.cta}
            disabled={pending || data.dialable === 0}
            aria-busy={pending || undefined}
            onClick={() => void begin()}
          >
            Queue starten · {data.dialable}
          </button>
          <button
            type="button"
            className={styles.ctaSecondary}
            onClick={() => setMode(mode === 'power' ? 'preview' : 'power')}
          >
            {mode === 'power' ? 'Auf Preview umstellen' : 'Auf Power umstellen'}
          </button>
          {data.skipped > 0 ? (
            <span className={styles.skipNote}>
              {data.skipped === 1 ? '1 Kontakt wird' : `${formatInteger(data.skipped)} Kontakte werden`}{' '}
              übersprungen: Status oder Zeitfenster passen nicht.
            </span>
          ) : null}
        </div>
      </section>
      <section className={styles.navyCard} aria-label="Erster Kontakt">
        <Eyebrow tone="inverse">Erster Kontakt</Eyebrow>
        <div className={styles.navyName}>{data.first.name}</div>
        {data.first.phone ? (
          <div className={styles.navyPhone}>{formatPhoneDisplay(data.first.phone)}</div>
        ) : null}
        <div className={styles.navyFacts}>
          <FactRow inverse label="Liste" value={data.first.listName} />
          <FactRow inverse label="Status" value={data.first.lifecycle.label} />
          <FactRow inverse label="Versuch" value={data.first.attempt} />
          {data.first.reachability ? (
            <FactRow inverse label="Erreichbar" value={data.first.reachability} />
          ) : null}
        </div>
        <p className={styles.navyNote}>
          Im Power-Modus wählt DAMOKLES sofort nach dem Start. Der Kontakt wird für andere Setter gesperrt,
          solange er in der Queue liegt. Jeder Kontakt bekommt den Ereignistyp der Liste, aus der er in die
          Queue kam.
        </p>
      </section>
    </div>
  );
}

function EmptyQueue({ data }: { data: Empty }) {
  const navigate = useNavigate();
  return (
    <div className={styles.emptyColumn}>
      <section className={styles.emptyCard} aria-labelledby="dialer-empty-title">
        <Eyebrow>{data.listName}</Eyebrow>
        <h2 id="dialer-empty-title" className={styles.h2}>
          Gerade kein Kontakt wählbar
        </h2>
        <p className={styles.emptyLead}>{emptyReason(data)}</p>
        {data.dialableAgainLabel !== null || data.thenDialable > 0 ? (
          <div className={styles.emptyFacts}>
            {data.dialableAgainLabel !== null ? (
              <Fact label="Wieder wählbar ab" value={data.dialableAgainLabel} />
            ) : null}
            <Fact label="Dann wählbar" value={formatInteger(data.thenDialable)} />
          </div>
        ) : null}
      </section>
      {data.alternative ? (
        <section className={styles.alternative} aria-label="Stattdessen">
          <div className={styles.alternativeText}>
            <Eyebrow tone="inverse">Stattdessen</Eyebrow>
            <div className={styles.alternativeName}>{data.alternative.name}</div>
            <div className={styles.alternativeMeta}>
              {formatInteger(data.alternative.dialable)} von {formatInteger(data.alternative.total)} jetzt
              wählbar
              {data.alternative.eventTypeName ? ` · Ereignistyp ${data.alternative.eventTypeName}` : ''}
            </div>
          </div>
          <button
            type="button"
            className={`${styles.cta} ${styles.alternativeCta}`}
            onClick={() => void navigate(`/dialer/preview/${data.alternative?.listId ?? ''}`)}
          >
            Diese Queue starten
          </button>
        </section>
      ) : (
        <div>
          <Button variant="secondary" onClick={() => void navigate('/dialer')}>
            Zurück zum Dialer
          </Button>
        </div>
      )}
    </div>
  );
}
